import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled, { useTheme } from 'styled-components';

import { colors, constants, media } from '../../../core';
import Form from '../../ui/Form';
import Icon from '../../ui/Icon';
import Text from '../../ui/Text';
import TextInput from '../../ui/TextInput';

const StyledText = styled(Text)`
  margin: 0;
  padding: 0;
  line-height: 1.2;
`;

const StyledList = styled.ul`
  padding-bottom: 27px;
  color: ${({ theme }) => theme.errorColor};
`;

const InputsWrapper = styled.div`
  width: 300px;
  margin: 16px auto;

  ${media.tabletPortrait} {
    width: 100%;
  }
`;

const ChangePasswordInputs = () => {
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { watch } = useFormContext();
  const password = watch(`newPassword`);

  const theme = useTheme();

  const [isLengthValid, setIsLengthValid] = useState(false);
  const [isUppercaseValid, setIsUppercaseValid] = useState(false);
  const [isLowercaseValid, setIsLowercaseValid] = useState(false);
  const [isOneNumberValid, setIsOneNumberValid] = useState(false);
  const [isSpecialCharacterValid, setIsSpecialCharacterValid] = useState(false);

  useEffect(() => {
    if (!password) {
      setIsLengthValid(false);
      setIsUppercaseValid(false);
      setIsLowercaseValid(false);
      setIsOneNumberValid(false);
      setIsSpecialCharacterValid(false);
    }
  }, [password]);

  const onValidate = (value: string) => {
    if (value.match(/^(?=.{8,})/)) {
      setIsLengthValid(true);
    } else {
      setIsLengthValid(false);
    }

    if (!!value.match(/^(?=.*[A-Z])/)) {
      setIsUppercaseValid(true);
    } else {
      setIsUppercaseValid(false);
    }

    if (!!value.match(/^(?=.*[a-z])/)) {
      setIsLowercaseValid(true);
    } else {
      setIsLowercaseValid(false);
    }

    if (!!value.match(/^(?=.*[0-9])/)) {
      setIsOneNumberValid(true);
    } else {
      setIsOneNumberValid(false);
    }

    if (!!value.match(/\W|_/g)) {
      setIsSpecialCharacterValid(true);
    } else {
      setIsSpecialCharacterValid(false);
    }

    return !!value.match(constants.PASSWORD_REGEX) || 'New password is not valid';
  };

  return (
    <React.Fragment>
      <InputsWrapper>
        <StyledText size="small">Your new password must have:</StyledText>
        <StyledList>
          <li>
            <StyledText size="small" color={isLengthValid ? colors.success : theme.errorColor}>
              &#x2022; a minimum of 8 characters,
            </StyledText>
          </li>
          <li>
            <StyledText size="small" color={isUppercaseValid ? colors.success : theme.errorColor}>
              &#x2022; at least one uppercase letter,
            </StyledText>
          </li>
          <li>
            <StyledText size="small" color={isLowercaseValid ? colors.success : theme.errorColor}>
              &#x2022; at least one lowercase letter,
            </StyledText>
          </li>
          <li>
            <StyledText size="small" color={isOneNumberValid ? colors.success : theme.errorColor}>
              &#x2022; one number,
            </StyledText>
          </li>
          <li>
            <StyledText
              size="small"
              color={isSpecialCharacterValid ? colors.success : theme.errorColor}
            >
              &#x2022; one special character.
            </StyledText>
          </li>
        </StyledList>
        <Form.Row column={1} noMargin rowGap="27px">
          <Form.Controller
            as={TextInput}
            type={showOldPassword ? 'text' : 'password'}
            name={`currentPassword`}
            animatedLabel="Current Password"
            placeholder={''}
            icon={
              <Icon
                type={showOldPassword ? 'view' : 'view-hide'}
                color={theme.textInputIconColor || colors.primary}
                onClick={() => setShowOldPassword(!showOldPassword)}
              />
            }
            rules={{
              required: 'This is required.',
            }}
          />

          <Form.Controller
            as={TextInput}
            type={showNewPassword ? 'text' : 'password'}
            name={`newPassword`}
            animatedLabel="New Password"
            placeholder={''}
            icon={
              <Icon
                type={showNewPassword ? 'view' : 'view-hide'}
                color={theme.textInputIconColor || colors.primary}
                onClick={() => setShowNewPassword(!showNewPassword)}
              />
            }
            rules={{
              required: 'This is required.',
              validate: onValidate,
            }}
          />

          <Form.Controller
            as={TextInput}
            type={showConfirmPassword ? 'text' : 'password'}
            name={`confirmPassword`}
            animatedLabel="Confirm Password"
            placeholder={''}
            icon={
              <Icon
                type={showConfirmPassword ? 'view' : 'view-hide'}
                color={theme.textInputIconColor || colors.primary}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              />
            }
            rules={{
              required: 'This is required.',
              validate: (value) => value === password || 'The passwords do not match',
            }}
          />
        </Form.Row>
      </InputsWrapper>
    </React.Fragment>
  );
};

export default ChangePasswordInputs;
